import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const SAVE_DONATION_SETTINGS_REQUEST = 'app/StripePayoutPage/SAVE_DONATION_SETTINGS_REQUEST';
export const SAVE_DONATION_SETTINGS_SUCCESS = 'app/StripePayoutPage/SAVE_DONATION_SETTINGS_SUCCESS';
export const SAVE_DONATION_SETTINGS_ERROR = 'app/StripePayoutPage/SAVE_DONATION_SETTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
  donationSettingsSaveInProgress: false,
  donationSettingsSaved: false,
  donationSettingsSaveError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case SAVE_DONATION_SETTINGS_REQUEST:
      return { ...state, donationSettingsSaveInProgress: true, donationSettingsSaved: false, donationSettingsSaveError: null };
    case SAVE_DONATION_SETTINGS_SUCCESS:
      return { ...state, donationSettingsSaveInProgress: false, donationSettingsSaved: true };
    case SAVE_DONATION_SETTINGS_ERROR:
      return { ...state, donationSettingsSaveInProgress: false, donationSettingsSaveError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

export const saveDonationSettingsRequest = () => ({
  type: SAVE_DONATION_SETTINGS_REQUEST,
});
export const saveDonationSettingsSuccess = () => ({
  type: SAVE_DONATION_SETTINGS_SUCCESS,
});
export const saveDonationSettingsError = e => ({
  type: SAVE_DONATION_SETTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const saveDonationSettings = values => (dispatch, getState, sdk) => {
  dispatch(saveDonationSettingsRequest());

  const { donationEnabled, donationSchoolId, donationSchoolName, donationPercentage } = values;

  return sdk.currentUser
    .updateProfile(
      { publicData: { donationEnabled, donationSchoolId, donationSchoolName, donationPercentage } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(currentUserShowSuccess(entities[0]));
      dispatch(saveDonationSettingsSuccess());
      return response;
    })
    .catch(e => {
      dispatch(saveDonationSettingsError(storableError(e)));
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};
